/**
 * subtitle-controller.js — Browser-Sidecar-WebVTT-Untertitel (F1 W4).
 *
 * zapd liefert Untertitel als HLS-SUBTITLES-Rendition (`subs-<N>.m3u8` +
 * `.vtt`-Segmente, Group "subs"). Der WASM/Canvas-Player demuxt sie NICHT
 * (rein Video+Audio) — diese Klasse laedt + parst + rendert die Sidecar-Spur
 * komplett im Main-Thread, parallel zur Decode-Pipeline (kein Eingriff in den
 * Worker-Hotpath).
 *
 * Modell (verifiziert gegen .116):
 *   - `subs-<N>.m3u8` ist eine VOLLE VOD-Playlist (alle .vtt-Segmente, ENDLIST)
 *     im Basis-gen 0 — keine gen-/on-demand-Komplexitaet wie bei Audio.
 *   - N = absoluter Container-Stream-Index (= playerSettings.subtitleTrack);
 *     im Master-Manifest steckt er in der Rendition-URI (`…/subs-<N>.m3u8`).
 *   - Cue-Timestamps sind stream-absolut (X-TIMESTAMP-MAP wird dennoch geehrt).
 *
 * Rendering: eigenes `.zap-cue-overlay`-DOM (gleiche CSS wie der Shell-Pfad in
 * shell/cue-overlay.js), Cue-Sichtbarkeit zeitfenster-basiert gegen
 * `video.currentTime`. textContent, NIE innerHTML — Cues stammen aus Medien.
 */

import { parseMasterPlaylist, parseMediaPlaylist } from '../demux/manifest-parser';
import { parseWebVtt } from './webvtt-parser.js';

const LOOKAHEAD_SEGS  = 3;   // wie viele .vtt-Segmente vor dem Playhead laden
const LOOKBEHIND_SEGS = 2;   // wie viele dahinter im Cache halten

export class SubtitleController {
  /** @param {{ stage: HTMLElement, video: HTMLMediaElement,
   *            getToken: () => string, masterUrl: string,
   *            initialTrack?: number }} o */
  constructor({ stage, video, getToken, masterUrl, initialTrack = -1 }) {
    this.video     = video;
    this.getToken  = typeof getToken === 'function' ? getToken : () => '';
    this.masterUrl = new URL(masterUrl, document.baseURI).toString();
    this.track     = -1;

    /** @type {import('../demux/manifest-parser').MediaPlaylist | null} */
    this.playlist = null;
    /** index→absolute subs-playlist-URL (aus dem Master). */
    this.renditionUrls = null;
    /** segIndex → parsed cues. */
    this.segCues   = new Map();
    this.loadedSegs  = new Set();
    this.loadingSegs = new Set();
    /** token, das eine laufende setTrack-Sequenz invalidiert (Switch-Race). */
    this._gen = 0;
    this._shown = '';
    this._destroyed = false;

    this.el = document.createElement('div');
    this.el.className = 'zap-cue-overlay';
    this.el.setAttribute('aria-live', 'polite');
    stage.appendChild(this.el);

    this._onTime = () => this._tick();
    this._onSeek = () => { this._clearDisplay(); };
    // 'seeked' re-runs one tick so cues reappear at the new position even while
    // PAUSED — 'timeupdate' does not fire while paused (e.g. scrub-preview), so
    // without this a paused seek would clear subtitles and never restore them.
    this._onSeeked = () => { this._tick(); };
    this.video.addEventListener('timeupdate', this._onTime);
    this.video.addEventListener('seeking', this._onSeek);
    this.video.addEventListener('seeked', this._onSeeked);

    if (Number.isFinite(initialTrack) && initialTrack >= 0) {
      // Best-effort; Fehler werden geloggt, brechen den Player nicht.
      this.setTrack(initialTrack).catch((e) =>
        console.warn('[subtitles] initial track load failed:', e?.message || e));
    }
  }

  /** Switch the active sub rendition (T4: NO session respawn). N<0 = off.
   *  Resolves to true on success/off, false if the rendition is missing or its
   *  playlist fails to load (so the caller can revert the UI selection).
   *  Superseded calls (newer setTrack/destroy) resolve true (not a failure). */
  async setTrack(n) {
    const next = Number.isFinite(n) ? Math.trunc(n) : -1;
    if (next === this.track) return true;
    this.track = next;
    const gen = ++this._gen;            // invalidates late replies of older tracks
    this._resetCaches();
    this._clearDisplay();
    if (next < 0) return true;          // subtitles off

    const url = await this._resolveRenditionUrl(next);
    if (gen !== this._gen || this._destroyed) return true;   // superseded
    if (!url) { console.warn(`[subtitles] no rendition for track ${next}`); return false; }

    let text;
    try { text = await this._fetch(url); }
    catch (e) { if (gen === this._gen) console.warn('[subtitles] subs-playlist load failed:', e?.message || e); return false; }
    if (gen !== this._gen || this._destroyed) return true;   // superseded

    this.playlist = parseMediaPlaylist(text, url);
    this._tick();                       // load around the current playhead now
    return true;
  }

  /**
   * Re-point at a new session master. Server-session-loss recovery
   * (refreshSession) swaps the engine playlist in place with a NEW sid WITHOUT
   * recreating this controller — so the cached master/rendition URLs would
   * point at the dead session and every subs-N/.vtt fetch would 404. Drops the
   * rendition cache and re-resolves the current track against the new master.
   */
  setSession(masterUrl) {
    if (this._destroyed || !masterUrl) return;
    const next = new URL(masterUrl, document.baseURI).toString();
    if (next === this.masterUrl) return;
    this.masterUrl     = next;
    this.renditionUrls = null;          // force re-resolve against the new master
    const cur = this.track;
    this._resetCaches();
    this._clearDisplay();
    this.track = -1;                    // so setTrack re-runs for the same index
    if (cur >= 0) {
      this.setTrack(cur).catch((e) =>
        console.warn('[subtitles] re-resolve after session refresh failed:', e?.message || e));
    }
  }

  destroy() {
    this._destroyed = true;
    this._gen++;
    this.video.removeEventListener('timeupdate', this._onTime);
    this.video.removeEventListener('seeking', this._onSeek);
    this.video.removeEventListener('seeked', this._onSeeked);
    try { this.el.remove(); } catch { /* noop */ }
    this._resetCaches();
  }

  // ── intern ─────────────────────────────────────────────────────────

  _resetCaches() {
    this.playlist = null;
    this.segCues.clear();
    this.loadedSegs.clear();
    this.loadingSegs.clear();
  }

  _clearDisplay() {
    this.el.replaceChildren();
    this._shown = '';
  }

  /** Master einmal parsen → index→URL-Map (Index aus der Rendition-URI). */
  async _resolveRenditionUrl(n) {
    if (!this.renditionUrls) {
      let text;
      try { text = await this._fetch(this.masterUrl); }
      catch (e) { console.warn('[subtitles] master load failed:', e?.message || e); return null; }
      const master = parseMasterPlaylist(text, this.masterUrl);
      const map = new Map();
      for (const r of master.renditions) {
        if (r.type !== 'subtitles' || !r.url) continue;
        const m = /subs-(\d+)\.m3u8/.exec(r.url);
        if (m) map.set(parseInt(m[1], 10), r.url);
      }
      this.renditionUrls = map;
    }
    return this.renditionUrls.get(n) || null;
  }

  _fetch(url) {
    const tok = this.getToken();
    const u = tok ? url + (url.includes('?') ? '&' : '?') + 'token=' + encodeURIComponent(tok) : url;
    // Deadline-Abort: zapd kann .vtt-Segmente an noch-nicht-transkodierten
    // Positionen mit gehaltenem 503 beantworten (Seek vor Transcode-Head).
    // Ohne Timeout verstopfen solche Requests den 6-Connection-Pool des
    // Browsers und wedgen den ganzen Sub-Loader. 8s, dann reject -> .finally
    // raeumt loadingSegs -> naechster tick versucht erneut, sobald produziert.
    const ctl = new AbortController();
    const timer = setTimeout(() => ctl.abort(), 8000);
    return fetch(u, { signal: ctl.signal }).then((r) => {
      if (!r.ok) throw new Error(`HTTP ${r.status} for ${url}`);
      return r.text();
    }).finally(() => clearTimeout(timer));
  }

  /** Segment-Index der `t` (s) enthaelt — Guess via targetDuration, dann verifiziert. */
  _segIndexForTime(t) {
    const segs = this.playlist.segments;
    if (segs.length === 0) return -1;
    const td = this.playlist.targetDurationSec || 4;
    let i = Math.min(segs.length - 1, Math.max(0, Math.floor(t / td)));
    // vorwaerts/rueckwaerts korrigieren (VFR/letztes kurzes Segment)
    while (i < segs.length - 1 && segs[i].startTimeSec + segs[i].durationSec <= t) i++;
    while (i > 0 && segs[i].startTimeSec > t) i--;
    return i;
  }

  _tick() {
    if (this._destroyed || this.track < 0 || !this.playlist) return;
    const now = this.video.currentTime || 0;
    const cur = this._segIndexForTime(now);
    if (cur < 0) return;

    const segs = this.playlist.segments;
    const lo = Math.max(0, cur - LOOKBEHIND_SEGS);
    const hi = Math.min(segs.length - 1, cur + LOOKAHEAD_SEGS);
    for (let i = lo; i <= hi; i++) this._loadSeg(i);

    // Free cache outside the window (bounded memory) — but KEEP a far segment
    // whose cue is still active at `now`: a long cue (sign/lyric) can span many
    // segments, and evicting its owning segment would blank it mid-display.
    for (const idx of this.loadedSegs) {
      if (idx < lo - LOOKBEHIND_SEGS || idx > hi + LOOKAHEAD_SEGS) {
        const cues = this.segCues.get(idx);
        if (cues && cues.some(c => now >= c.startSec && now < c.endSec)) continue;
        this.segCues.delete(idx);
        this.loadedSegs.delete(idx);
      }
    }
    this._render(now);
  }

  _loadSeg(i) {
    if (this.loadedSegs.has(i) || this.loadingSegs.has(i)) return;
    const seg = this.playlist.segments[i];
    if (!seg) return;
    this.loadingSegs.add(i);
    const gen = this._gen;
    this._fetch(seg.url).then((text) => {
      if (gen !== this._gen || this._destroyed) return;
      this.segCues.set(i, parseWebVtt(text));
      this.loadedSegs.add(i);
      this._render(this.video.currentTime || 0);
    }).catch(() => { /* transient .vtt-fetch fail -> naechster tick reloaded */ })
      .finally(() => { this.loadingSegs.delete(i); });
  }

  _render(now) {
    // Collect cues active at `now` across ALL loaded segments (the set is
    // bounded by _tick's prune). Scanning every loaded segment — not just a
    // fixed window around `now` — lets a long cue held in a far segment still
    // render. Dedupe by cue identity (start:end:text): zapd emits a
    // boundary-spanning cue in BOTH adjacent .vtt segments, so without dedup
    // the same text would appear twice (Team-D hint).
    const seen = new Map();
    for (const cues of this.segCues.values()) {
      for (const c of cues) {
        if (now >= c.startSec && now < c.endSec) {
          seen.set(c.startSec + ':' + c.endSec + ':' + c.text, c.text);
        }
      }
    }
    const active = [...seen.values()];
    // Idempotent: nur neu rendern, wenn sich der sichtbare Text aendert.
    const key = [...seen.keys()].join('|');
    if (key === this._shown) return;
    this._shown = key;
    this.el.replaceChildren();
    for (const text of active) {
      const node = document.createElement('div');
      node.className = 'zap-cue';
      node.textContent = text;        // NIE innerHTML
      this.el.appendChild(node);
    }
  }
}
